import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useUserAuth } from '../contexts/UserAuthContext';

const DISCORD_URL = process.env.REACT_APP_DISCORD_URL || 'https://discord.gg/hosting-community';

function DiscordIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

export function Navbar() {
  const { lang, switchLang, t } = useLanguage();
  const { user, logout } = useUserAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); setMobileOpen(false); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToListing = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById('anbieter-werden');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || mobileOpen
          ? 'bg-[#020817]/95 backdrop-blur-md border-b border-slate-800 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <a
            href="/"
            data-testid="nav-logo"
            className="flex items-center gap-1 transition-opacity hover:opacity-80 select-none"
          >
            <span className="text-xl font-extrabold tracking-tight text-white uppercase" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.06em' }}>
              Hosting-
            </span>
            <span className="text-xl font-extrabold tracking-tight text-cyan-400 uppercase" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.06em' }}>
              Community
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-3">
            {/* Als Anbieter listen */}
            <a
              href="#anbieter-werden"
              onClick={scrollToListing}
              data-testid="nav-listing-btn"
              className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/20 hover:border-cyan-500/60 transition-colors"
            >
              {t.listing.badge}
            </a>

            {/* Produkte */}
            <Link
              to="/produkte"
              data-testid="nav-products-btn"
              className="px-3 py-1.5 text-slate-300 hover:text-white text-sm font-medium transition-colors"
            >
              {t.productsPage?.navLabel || 'Produkte'}
            </Link>

            {/* Discord */}
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-discord-btn"
              className="flex items-center gap-2 px-3 py-1.5 bg-[#5865F2]/15 border border-[#5865F2]/30 text-[#7289da] rounded-lg text-sm font-medium hover:bg-[#5865F2]/25 hover:border-[#5865F2]/60 transition-colors"
            >
              <DiscordIcon className="w-4 h-4" />
              Discord
            </a>

            {/* Language switcher */}
            <div
              data-testid="language-switcher"
              className="flex items-center gap-1 bg-slate-800 rounded-full p-1"
            >
              <button
                onClick={() => switchLang('de')}
                data-testid="lang-de"
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  lang === 'de' ? 'bg-cyan-500 text-slate-950 shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
              >
                DE
              </button>
              <button
                onClick={() => switchLang('en')}
                data-testid="lang-en"
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  lang === 'en' ? 'bg-cyan-500 text-slate-950 shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
              >
                EN
              </button>
            </div>

            {/* User auth */}
            {user ? (
              <div className="flex items-center gap-1">
                <Link to="/account" data-testid="nav-account-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:text-white hover:border-slate-600 transition-colors">
                  <User className="w-3.5 h-3.5" />
                  {user.display_name?.split(' ')[0] || t.user.navAccount}
                </Link>
                <button onClick={handleLogout} data-testid="nav-logout-btn"
                  className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login-btn"
                className="px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium hover:text-white hover:border-slate-600 transition-colors">
                {t.user.navLogin}
              </Link>
            )}
          </div>

          {/* Mobile: language pills always visible + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            {/* Language switcher – always visible on mobile */}
            <div
              data-testid="language-switcher"
              className="flex items-center gap-1 bg-slate-800 rounded-full p-1"
            >
              <button
                onClick={() => switchLang('de')}
                data-testid="lang-de"
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  lang === 'de' ? 'bg-cyan-500 text-slate-950' : 'text-slate-400'
                }`}
              >
                DE
              </button>
              <button
                onClick={() => switchLang('en')}
                data-testid="lang-en"
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  lang === 'en' ? 'bg-cyan-500 text-slate-950' : 'text-slate-400'
                }`}
              >
                EN
              </button>
            </div>

            {/* Hamburger */}
            <button
              className="text-slate-400 hover:text-white transition-colors p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileOpen && (
          <div
            className="md:hidden border-t border-slate-800 py-4 space-y-2"
            data-testid="mobile-menu"
          >
            {/* Als Anbieter listen */}
            <a
              href="#anbieter-werden"
              onClick={scrollToListing}
              data-testid="mobile-listing-btn"
              className="flex items-center px-3 py-2.5 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium w-full"
            >
              {t.listing.badge}
            </a>

            {/* Produkte */}
            <Link
              to="/produkte"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-products-btn"
              className="flex items-center px-3 py-2.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg text-sm font-medium w-full"
            >
              {t.productsPage?.navLabel || 'Produkte'}
            </Link>

            {/* Discord */}
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-discord-btn"
              className="flex items-center gap-2 px-3 py-2.5 bg-[#5865F2]/15 border border-[#5865F2]/30 text-[#7289da] rounded-lg text-sm font-medium w-full"
              onClick={() => setMobileOpen(false)}
            >
              <DiscordIcon className="w-4 h-4" />
              Discord
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}
